/**
 * ENG-5859: polite-decline replies when sender_policy drops a message.
 *
 * Background: when slack-channel / teams-channel inbound filters drop a
 * message with `reason: 'sender_policy'` (either mode mismatch or
 * internal_only), today the sender sees nothing. Diagnosing "why isn't
 * the agent responding?" requires reading MCP stderr. This module
 * decides whether to post a one-line reply explaining the block, and
 * what copy to use.
 *
 * Pure functional core:
 *   - classifySlackPolicyBlock — pinpoint which axis caused the drop
 *     (internal_only first, then mode). Caller only invokes when the
 *     filter has already returned `reason: 'sender_policy'`, so there
 *     IS always a real cause.
 *   - classifyTeamsPolicyBlock — same shape for Teams (tenant boundary
 *     instead of workspace).
 *   - politeDeclineCopy — maps reason → default decline string. Single
 *     source of truth so the same blocked sender hitting Slack + Teams
 *     gets the same wording.
 *   - decideDeclineReply — rate-limit decision keyed on
 *     {channel,sender,reason} with an injectable Map + clock. Pure so
 *     tests don't have to wait real time.
 *
 * Why differentiate by reason: mild information disclosure, but a
 * confused sender knowing "oh I'm not the manager" or "this is cross-
 * workspace" is the difference between giving up and routing to the
 * right person. Without it, support gets "the agent ignored me"
 * tickets indistinguishable from real outages.
 *
 * Why rate-limit per-sender: a confused user pinging repeatedly (or
 * a webhook firing on a loop) shouldn't spam the channel with
 * decline replies and burn bot rate-limit quota.
 */

import type { SlackMessageEvent, SenderPolicy as SlackSenderPolicy } from './slack-inbound-filter.js';
import type { TeamsActivityInput, TeamsSenderPolicy } from './teams-inbound-filter.js';

export type SenderPolicyBlockReason =
  | 'internal_only'
  | 'mode_manager_only'
  | 'mode_team_only'
  // ENG-10440: team_only whose resolved member allowlist for THIS channel is
  // empty. Distinct from `mode_team_only` because the two facts are opposite:
  // `mode_team_only` means "the allowlist exists and you are not on it", this
  // means "the allowlist resolved to nobody, so no human can reach the agent at
  // all". robin's team_only drop was the second shape and presented as the
  // first, which told a real team member she was not one.
  | 'mode_team_only_no_principals'
  // ENG-10663: manager_only whose manager does not resolve to a reachable
  // principal on this surface — a null `reports_to`, a reports-to chain that
  // never terminates in a human, or a person row with no linked account. The
  // mirror of `mode_team_only_no_principals`, and it exists for the same
  // reason: `mode_manager_only` tells the sender "you are not the manager",
  // which is a lie when the truth is "this agent has no manager, so it can
  // speak to nobody at all". One is fixed by finding the manager; the other
  // needs an admin to set `reports_to`.
  | 'mode_manager_only_no_principals'
  | 'mode_team_agents_only'
  | 'mode_agents_only';

/**
 * ENG-10440: the team_only sub-reason. Shared by both classifiers so the
 * "empty allowlist" rule cannot drift between Slack and Teams.
 */
function teamOnlyReason(allowlist: readonly string[] | undefined): SenderPolicyBlockReason {
  return allowlist && allowlist.length > 0 ? 'mode_team_only' : 'mode_team_only_no_principals';
}

/**
 * Pinpoint which axis caused a Slack sender_policy drop. Caller MUST
 * have already gotten `{ forward: false, reason: 'sender_policy' }` from
 * `decideSenderPolicyForward` before invoking; otherwise the
 * classification is meaningless (mode 'all' alone never blocks).
 *
 * The order mirrors `decideSenderPolicyForward`: internal_only is
 * checked first because the filter applies it first. If both axes
 * would have blocked, we attribute to internal_only — that's the more
 * specific signal the sender can act on ("cross-workspace, not the
 * mode").
 */
export function classifySlackPolicyBlock(
  evt: SlackMessageEvent,
  policy: SlackSenderPolicy,
): SenderPolicyBlockReason {
  if (
    policy.internalOnly &&
    (!policy.homeTeamId || evt.team !== policy.homeTeamId)
  ) {
    return 'internal_only';
  }
  switch (policy.mode) {
    case 'manager_only':
      return 'mode_manager_only';
    case 'team_only':
      return teamOnlyReason(policy.teamPrincipalSlackUserIds);
    case 'team_agents_only':
      return 'mode_team_agents_only';
    case 'agents_only':
      return 'mode_agents_only';
    case 'all':
      // mode:'all' alone never blocks, so if we got here without
      // internal_only matching, the only remaining cause is the
      // internal_only fail-closed branch (missing homeTeamId) that the
      // first if-arm already returned for. Defensive fallback.
      return 'internal_only';
  }
}

/**
 * Teams equivalent. Tenant boundary (channelData.tenant.id /
 * conversation.tenantId) replaces Slack's workspace `team`.
 */
export function classifyTeamsPolicyBlock(
  activity: TeamsActivityInput,
  policy: TeamsSenderPolicy,
): SenderPolicyBlockReason {
  const senderTenantId =
    activity.channelData?.tenant?.id ?? activity.conversation?.tenantId;
  if (
    policy.internalOnly &&
    (!policy.homeTenantId ||
      policy.homeTenantId === 'common' ||
      senderTenantId !== policy.homeTenantId)
  ) {
    return 'internal_only';
  }
  switch (policy.mode) {
    case 'manager_only':
      return 'mode_manager_only';
    case 'team_only':
      return teamOnlyReason(policy.teamPrincipalAadObjectIds);
    case 'team_agents_only':
      return 'mode_team_agents_only';
    case 'agents_only':
      return 'mode_agents_only';
    case 'all':
      return 'internal_only';
  }
}

/**
 * Default polite-decline copy. Single source of truth so a user
 * blocked on both Slack and Teams gets the same wording.
 *
 * Kept short on purpose — these reach senders who didn't expect a
 * response in the first place. Long apology paragraphs read passive-
 * aggressive in chat.
 */
export function politeDeclineCopy(reason: SenderPolicyBlockReason): string {
  switch (reason) {
    case 'internal_only':
      return "Sorry, I can only respond to people inside our organisation. This conversation appears to be from outside.";
    case 'mode_manager_only':
      return "Sorry, I only respond to my manager in this channel.";
    case 'mode_team_only':
      // ENG-5872 PR B: refined from PR A's "Sorry, I only respond to people
      // on my team in this channel." Council on PR B flagged "my team" as
      // ambiguous in Slack — end-users equate "team" with the Slack
      // workspace, not the Augmented Team. Rephrasing in the third person
      // ("this agent only replies to its team members") shifts the
      // referent off the Slack-workspace reading and onto the agent's
      // operational team — which is what the mode actually gates. Still
      // doesn't name the resolution mechanism (intentional: senders don't
      // need to know about contact_preferences joins).
      return "Sorry, this agent only replies to its team members.";
    case 'mode_team_only_no_principals':
      // ENG-10440: the sender may well BE a team member, so the mode_team_only
      // copy ("only replies to its team members") would tell them something
      // false. What is true: nobody's account in this channel is linked to the
      // agent, and that needs an admin, not the sender. Deliberately does not
      // claim an operator was alerted — this copy cannot know that it was.
      return "Sorry, I can't reply to anyone in this channel yet: none of my team members' accounts here are linked to me. An admin needs to link them first.";
    case 'mode_manager_only_no_principals':
      // ENG-10663: the sender may well BE the manager, so the mode_manager_only
      // copy would tell them something false. What is true: nobody is currently
      // configured as this agent's manager, so it can converse with no one —
      // and that needs an admin, not the sender. Deliberately does not claim
      // anyone was alerted; this copy cannot know that.
      return "Sorry, I can't reply to anyone right now: I'm set to talk only to my manager, and no manager is configured for me. An admin needs to set who I report to.";
    case 'mode_team_agents_only':
      return "Sorry, I only respond to agents on my team in this channel.";
    case 'mode_agents_only':
      return "Sorry, I only respond to other agents in this channel.";
  }
}

/**
 * ENG-10440: was this Slack message addressed to the agent? Only an addressed
 * message may earn a polite decline.
 *
 * Why this gate exists: the Slack decline runs at the access decision, BEFORE
 * the mention_only engagement gate. While `sameOrg` was false for most orgs
 * that ordering was invisible. Now that same-workspace senders are recognised,
 * an ungated decline would answer EVERY un-addressed channel message a
 * restricted agent can see - a chatty bot replacing a silent one.
 *
 * Addressed means one of:
 *   - an `app_mention` event;
 *   - a 1:1 DM (channel id starts with `D`);
 *   - the text carries an explicit `<@botUserId>` mention.
 * Everything else (channel chatter, group DMs without a mention, auto-followed
 * thread replies) is not, and stays silent exactly as before. An unknown
 * `botUserId` (pre-connect) fails closed on the text branch only; the
 * `app_mention` and DM branches do not need it.
 */
export function isSlackDeclineAddressed(input: {
  eventType: string | undefined;
  channelId: string | undefined;
  text: unknown;
  botUserId: string | null;
}): boolean {
  if (input.eventType === 'app_mention') return true;
  if (typeof input.channelId === 'string' && input.channelId.startsWith('D')) return true;
  return (
    !!input.botUserId &&
    typeof input.text === 'string' &&
    input.text.includes(`<@${input.botUserId}>`)
  );
}

export interface DeclineRateLimitInput {
  /**
   * Caller-owned map: key → last-reply timestamp (ms). The pure
   * decision mutates this map (writes a new timestamp on admit) so
   * the caller doesn't have to remember a second step. In-process
   * lifetime — restarts naturally reset the cache, which is the
   * desired behaviour for a "I changed the policy, give me a chance
   * to test it" workflow.
   */
  cache: Map<string, number>;
  /**
   * Composite key: `${channelId}|${senderId}|${reason}`. Per-reason
   * keys mean a sender who's blocked on internal_only AND then later
   * tries from a permitted workspace but hits manager_only will get
   * BOTH messages — once each — rather than being silenced on the
   * second after the first burned the cooldown.
   */
  key: string;
  /** Cooldown window in ms. Default suggested at the call site (30 min). */
  cooldownMs: number;
  /** Clock injected from caller — production uses `Date.now()`. */
  now: number;
}

export interface DeclineRateLimitDecision {
  /** True iff a reply should be posted now. */
  reply: boolean;
  /** Set when `reply` is false — how many ms left on the cooldown. */
  remainingMs?: number;
}

export function decideDeclineReply(
  input: DeclineRateLimitInput,
): DeclineRateLimitDecision {
  const last = input.cache.get(input.key);
  if (last !== undefined) {
    const elapsed = input.now - last;
    if (elapsed < input.cooldownMs) {
      return { reply: false, remainingMs: input.cooldownMs - elapsed };
    }
  }
  input.cache.set(input.key, input.now);
  return { reply: true };
}

/**
 * Build the composite cache key. Exported so call sites and tests
 * use the exact same shape — drift here would silently bypass the
 * rate limit ("oh I keyed off `<channel>:<user>` not `<channel>|<user>|<reason>`").
 */
export function declineCacheKey(
  channelId: string,
  senderId: string,
  reason: SenderPolicyBlockReason,
): string {
  return `${channelId}|${senderId}|${reason}`;
}

/**
 * Read the cooldown from env. Default 30 minutes. Lets ops dial it
 * down during incident response (e.g. tightening policy + wanting
 * immediate verification) or up if the channel is noisy.
 */
export function readDeclineCooldownMs(
  envVarName: string,
  defaultSeconds = 1800,
): number {
  const raw = process.env[envVarName];
  if (!raw) return defaultSeconds * 1000;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return defaultSeconds * 1000;
  return parsed * 1000;
}
